<template>
  <div>
    <div v-if="isGeneratingSlides" class="is-submitting__content">
      <div class="mt-9 flex flex-col items-center">
        <div class="text-base-100">
          {{ $i18nPresenter.t("Our robot's on it 🤖 Don't go anywhere!") }}
        </div>
        <FakeProgressbar :resetProgressBar="resetProgressBar" />
      </div>
    </div>
    <div
      v-if="isNotGeneratingSlidesAndMightHaveError"
      class="search-result-inner"
      id="ai-slides-generator-result-layout"
    >
      <div v-if="chatResponseError" class="text-center mt-15">
        <AIStumped />
        <aha-button variant="base" @click.stop="retryAISlidesGenerator()" class="mt-4">
          <aha-icon
            name="reset"
            width="15"
            height="15"
            viewBox="0 0 16 16"
            class="mr-1"
          />
          {{ $i18nPresenter.t('Retry') }}
        </aha-button>
      </div>
      <div class="content-list" v-if="isNotGeneratingAndHasSlides">
        <div v-for="(response, index) in chatResponse" :key="response.id">
          <AIGeneratedPickAnswerContent
            v-if="response.type === AIResponseType.PICK_ANSWER"
            :response="response"
            :index="index"
            @handleAddSlideToPresentation="handleAddSlideToPresentation"
            :isModalContent="isModalContent"
            class="content-item"
          />
          <AIGeneratedFreestyleContent
            v-if="response.type === AIResponseType.FREESTYLE"
            :response="response"
            :index="index"
            @handleAddSlideToPresentation="handleAddSlideToPresentation"
            :isModalContent="isModalContent"
            class="content-item"
          />
          <AIGeneratedTypeAnswerContent
            v-if="response.type === AIResponseType.TYPE_ANSWER"
            :response="response"
            :index="index"
            @handleAddSlideToPresentation="handleAddSlideToPresentation"
            :isModalContent="isModalContent"
            class="content-item"
          />
        </div>
        <div
          class="content-list__bottom"
          v-if="isContentListBottomVisible"
          :class="{'h-12 pb-4': hasStoppedGenerating}"
        >
          <div v-if="isSingleSlideGenerationError" class="text-center mt-15 h-0">
            <AIStumped :customText="customAISlideStumpedText" class="pb-12" />
          </div>
          <aha-spinner v-if="isContentListLoading" />
          <p v-if="hasStoppedGenerating" class="text-base-80" :class="{'mt-12': !chatResponse.length}">
            {{ $i18nPresenter.t('Generation stopped.') }}
          </p>
        </div>
      </div>
      <AIFeedbackLoading v-if="isSubmittingFeedback" :resetProgressBar="resetProgressBar" />
    </div>
  </div>
</template>

<script>
// utils
import { mapState } from 'vuex'
import utils from '@/util'

// constants
import eventCategories from '@/constant/eventCategories'

// components
import FakeProgressbar from '../FakeProgressbar.vue'
import AIGeneratedPickAnswerContent from '@/components-v2/presentation-editor/new-slide/OpenAI/AIGeneratedPickAnswerContent.vue'
import AIGeneratedFreestyleContent from '@/components-v2/presentation-editor/new-slide/OpenAI/AIGeneratedFreestyleContent.vue'
import AIGeneratedTypeAnswerContent from '@/components-v2/presentation-editor/new-slide/OpenAI/AIGeneratedTypeAnswerContent.vue'
import AIStumped from '@/components-v2/presentation-editor/new-slide/OpenAI/AIStumped.vue'
import AIFeedbackLoading from '@/components-v2/openAI/AIFeedbackLoading.vue'
import AIResponseType from '@/constant/aiResponseType'

export default {
  components: {
    FakeProgressbar,
    AIGeneratedPickAnswerContent,
    AIGeneratedFreestyleContent,
    AIGeneratedTypeAnswerContent,
    AIStumped,
    AIFeedbackLoading,
  },
  props: {
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    isSubmittingFeedback: {
      type: Boolean,
      default: false,
    },
    resetProgressBar: {
      type: Number,
      default: 0,
    },
    chatResponseError: {
      type: Boolean,
      default: false,
    },
    chatResponse: {
      type: Array,
      default: () => [],
    },
    isContentListLoading: {
      type: Boolean,
      default: false,
    },
    hasStoppedGenerating: {
      type: Boolean,
      default: false,
    },
    isModalContent: {
      type: Boolean,
      default: false,
    },
    isReturnedSlideError: {
      type: Boolean,
      default: false,
    },
    customAISlideStumpedText: {
      type: Array,
      default: () => [],
    },
    prompt: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      AIResponseType,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      presentation: (state) => state.presentation,
    }),

    isGeneratingSlides() {
      return this.isSubmitting && !this.isSubmittingFeedback && !this.chatResponseError
    },
    isNotGeneratingSlidesAndMightHaveError() {
      return !this.isSubmitting
    },
    isNotGeneratingAndHasSlides() {
      return !this.isSubmitting && this.chatResponse
    },
    isSingleSlideGenerationError() {
      return this.isReturnedSlideError && !this.chatResponseError
    },
    isContentListBottomVisible() {
      return this.isSingleSlideGenerationError || this.isContentListLoading || this.hasStoppedGenerating
    },
  },
  methods: {
    handleAddSlideToPresentation(response) {
      this.$emit('handleAddSlideToPresentation', response)
    },
    retryAISlidesGenerator() {
      this.$emit('handleGenerateSlides')
      const eventAction = 'AI Slide Generator > Generate > Retry'
      const trackData = {
        eventAction,
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        eventLabel: this.prompt,
        presentationId: this.presentation.id,
        userId: this.currentUser.id,
      }
      utils.trackGA4AndMixpanel(eventAction, trackData)
    },
  },
}
</script>

<style lang="scss" scoped>
.mt-12 {
  margin-top: 3rem!important; // overwrite CSS bootstrap
}
.is-submitting__content {
  @apply flex justify-center w-full h-full;
  @media (max-width: 798px) {
    @apply text-center px-4;
  }
}
.search-result-inner {
  padding: 0 1rem;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  -ms-overflow-style: auto;
}

.content-list {
  .content-item {
    @apply shadow rounded-lg my-4;
    @apply w-full;
    color: #000;
    background-color: #fff;
    &::v-deep {
      ul {
        list-style: disc;
        padding-left: 1rem;
      }
      p:not(:last-child) {
        padding-bottom: 0.75rem;
      }
    }
  }
  .content-list__bottom {
    @apply w-full h-8 flex items-center justify-center;
  }
}
</style>
